/**
 * SlideOverlay - Mobile-only full-screen overlay that slides in from the side.
 *
 * Used for views that should appear "on top" of the app shell with a smooth
 * push-in transition (e.g. ComposePostModal, MessagesOverlay, PostOverlay).
 * Stays mounted during the exit animation so the slide-out plays before
 * unmount.
 *
 * Hidden at md+ breakpoints; desktop layouts should render the same content
 * inline rather than via this overlay.
 *
 * Multiple overlays can be stacked (e.g. messages list + open thread). Each
 * stack level can pass `zIndex` to land above the previous one.
 *
 * Dismiss triggers:
 *  - Header "← Back" button
 *  - Backdrop tap
 *  - Escape key
 *  - Browser / hardware Back button (via `useOverlayBackClose`)
 *  - Horizontal swipe in the dismiss direction (via `useSwipeToClose`)
 */

import React, { useEffect } from 'react';
import useSlideTransition from '../hooks/useSlideTransition';
import useOverlayBackClose from '../hooks/useOverlayBackClose';
import useSwipeToClose from '../hooks/useSwipeToClose';

function SlideOverlay({
  open,
  title,
  onClose,
  children,
  testId = 'slide-overlay',
  direction = 'right',
  zIndex = 40,
  contentClassName = 'flex-1 min-h-0 overflow-y-auto',
}) {
  const { mounted, shown } = useSlideTransition(open);
  useOverlayBackClose(open, onClose);
  const { ref: panelRef, dragX } = useSwipeToClose({
    open,
    direction,
    onClose,
  });

  useEffect(() => {
    if (!open) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!mounted) return null;

  const closedTransform = direction === 'left' ? '-translate-x-full' : 'translate-x-full';
  const dragging = dragX !== 0;
  // While the finger is on the panel we need the transform to follow the touch
  // directly; once the finger lifts we let the Tailwind `transition-transform`
  // snap-back animation play.
  const panelStyle = dragging ? { transform: `translateX(${dragX}px)` } : undefined;
  const transitionClass = dragging ? 'transition-none' : 'transition-transform duration-300 ease-zen';

  return (
    <div
      className="md:hidden fixed inset-0"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-title`}
      data-testid={testId}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className={`absolute inset-0 bg-void/70 backdrop-blur-sm transition-opacity duration-300 ease-zen ${
          shown ? 'opacity-100' : 'opacity-0'
        }`}
        data-testid={`${testId}-backdrop`}
      />
      <aside
        ref={panelRef}
        style={panelStyle}
        className={`absolute inset-0 bg-void flex flex-col pt-safe-top pb-safe-bottom ${transitionClass} ${
          shown ? 'translate-x-0' : closedTransform
        }`}
        data-testid={`${testId}-panel`}
      >
        <div className="flex-shrink-0 flex items-center gap-3 px-4 py-3 border-b border-shadow">
          <button
            type="button"
            onClick={onClose}
            className="text-ether text-sm hover:underline"
            data-testid={`${testId}-back`}
          >
            ← Back
          </button>
          <h2
            id={`${testId}-title`}
            className="font-display text-lg font-bold text-ash flex-1 truncate"
          >
            {title}
          </h2>
        </div>
        <div className={contentClassName}>{children}</div>
      </aside>
    </div>
  );
}

export default SlideOverlay;
